// @greenlight-spec: 10-security-compliance.md#soc-2-evidence-view

import type { Soc2Evidence } from '@greenlight/contract';

import { escapeCsvCell as escape } from './csv.js';

/** The payload verbatim — what a compliance platform ingests. */
export function buildSoc2Json(evidence: Soc2Evidence): string {
  return `${JSON.stringify(evidence, null, 2)}\n`;
}

const CONTROL_HEADER = [
  'control',
  'family',
  'title',
  'coverage',
  'mechanism',
  'greenlight_checks',
] as const;

const APP_HEADER = [
  'app_slug',
  'app_name',
  'commit_sha',
  'run_status',
  'gate_decision',
  'completed_at',
  'pull_request',
  'checks',
  'sbom_available',
  'sbom_expires_at',
] as const;

/**
 * Two tables in one file: the control mapping, then the per-app evidence.
 *
 * A spreadsheet is what an auditor asks for, and it has no way to express one
 * of these nested inside the other — so they are written as separate blocks
 * with their own header rows rather than flattened into a join that repeats
 * every control once per app.
 */
export function buildSoc2Csv(evidence: Soc2Evidence): string {
  const lines: string[] = [];
  lines.push(
    escape(`# ${evidence.framework} control coverage — generated ${evidence.generated_at}`),
  );
  lines.push(escape(`# ${evidence.scope_note}`));
  lines.push('');
  lines.push(CONTROL_HEADER.join(','));
  for (const row of evidence.controls) {
    lines.push(
      [
        row.control,
        row.family,
        row.title,
        // The header says coverage, so write the org's resolved coverage. Writing
        // `kind` told a compliance platform the control is a gate without saying
        // whether it holds merges here, which is the question it is ingesting for.
        row.coverage,
        row.mechanism,
        row.policy_ids.join(' '),
      ]
        .map(escape)
        .join(','),
    );
  }
  lines.push('');
  lines.push(APP_HEADER.join(','));
  for (const app of evidence.apps) {
    // An app that never deployed has no gate decision to have gone unrecorded and
    // no SBOM to have lapsed. Writing "not recorded" and "no" would describe a
    // build that never happened, and an auditor reading only the export would see
    // missing evidence where there is simply no deploy — the page shows an em dash
    // here for the same reason.
    const deployed = app.run_id !== null;
    lines.push(
      [
        app.app_slug,
        app.app_name,
        app.commit_sha ?? '',
        app.run_status ?? '',
        // Never blank-as-allowed: an absent decision is its own state.
        // Empty rather than a third token: the row already carries an empty
        // commit and run status, so "never deployed" reads from the row, and a
        // yes/no column stays yes/no for whatever ingests it.
        !deployed
          ? ''
          : app.gate_allowed === null
            ? 'not recorded'
            : app.gate_allowed
              ? 'allowed'
              : 'denied',
        app.completed_at ?? '',
        app.pull_request_number === null ? '' : `#${app.pull_request_number}`,
        app.checks.map((c) => `${c.name}=${c.status}`).join(' '),
        !deployed ? '' : app.sbom.available ? 'yes' : 'no',
        app.sbom.expires_at ?? '',
      ]
        .map(escape)
        .join(','),
    );
  }
  return `${lines.join('\n')}\n`;
}
